/* ═══════════════════════ LIVE FLYSTATUS ═══════════════════════
   Henter live status fra /api/flightstatus (AviationStack) på forespørsel.
   Faller pent tilbake til rutetider + FlightAware når nøkkel mangler.
════════════════════════════════════════════════════════════════ */
import { S } from './state.js';
import { esc } from './utils.js';

export const FLIGHT_LIVE = {};

export async function checkFlight(num) {
  const k = String(num || "").toUpperCase();
  if (!k || FLIGHT_LIVE[k]?.loading) return;
  FLIGHT_LIVE[k] = { loading: true };
  if (window.renderPage) window.renderPage();
  try {
    const r = await fetch("/api/flightstatus?flight=" + encodeURIComponent(k));
    FLIGHT_LIVE[k] = { loading: false, data: await r.json(), ts: Date.now() };
  } catch { FLIGHT_LIVE[k] = { loading: false, error: true }; }
  if (window.renderPage) window.renderPage();
}

const STATUS_NB = {
  scheduled: ["Rutetid", "var(--t2)"], active: ["I luften", "var(--green)"],
  landed: ["Landet", "var(--sky)"], cancelled: ["INNSTILT", "var(--red)"],
  incident: ["Avvik", "var(--red)"], diverted: ["Omdirigert", "var(--amber)"],
  delayed: ["Forsinket", "var(--amber)"],
};
const hhmm = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export function renderLiveFlight(num) {
  const s = FLIGHT_LIVE[String(num).toUpperCase()];
  if (!s) return "";
  const box = (html) => `<div style="margin-top:8px;padding:9px 11px;background:var(--bg2);border:1px solid var(--border);border-radius:8px">${html}</div>`;
  if (s.loading) return box(`<span style="font-size:11.5px;color:var(--t3)">Henter live status…</span>`);
  if (s.error || !s.data) return box(`<span style="font-size:11.5px;color:var(--t3)">Kunne ikke hente status. Prøv FlightAware-lenken.</span>`);
  const d = s.data;
  if (d.configured === false) return box(`<span style="font-size:11.5px;color:var(--gold2)">⚙️ Live status ikke aktivert ennå (mangler API-nøkkel i Vercel). Bruk FlightAware-lenken under.</span>`);
  if (d.error) return box(`<span style="font-size:11.5px;color:var(--t3)">Leverandørfeil: ${esc(String(d.error).slice(0, 60))}. Bruk FlightAware.</span>`);
  if (d.found === false) return box(`<span style="font-size:11.5px;color:var(--t3)">Ingen live data ennå — vises typisk fra ~24 t før avgang.</span>`);
  const [lbl, col] = STATUS_NB[d.status] || ["Ukjent", "var(--t2)"];
  const depDelay = d.dep?.delay ? ` <span style="color:var(--amber)">+${d.dep.delay} min</span>` : "";
  const row = (label, o) => o ? `<div style="font-size:11.5px;color:var(--t1);margin-top:3px"><span style="color:var(--t3)">${label}:</span> ${esc(o.airport || "")}${o.terminal ? " · Term " + esc(o.terminal) : ""}${o.gate ? " · Gate " + esc(o.gate) : ""}${(o.estimated && hhmm(o.estimated) !== hhmm(o.scheduled)) ? ` · <span style="color:var(--amber)">est ${hhmm(o.estimated)}</span> <s style="color:var(--t3)">${hhmm(o.scheduled)}</s>` : (o.scheduled ? " · " + hhmm(o.scheduled) : "")}</div>` : "";
  return box(`<div style="display:flex;justify-content:space-between;align-items:center">
      <span style="font-size:12px;font-weight:800;color:${col}">● ${esc(lbl)}${depDelay}</span>
      <span style="font-size:9.5px;color:var(--t3)">oppdatert ${s.ts ? hhmm(new Date(s.ts).toISOString()) : ""}</span>
    </div>${row("Fra", d.dep)}${row("Til", d.arr)}`);
}

// Nedtelling til neste avgang + innsjekk-påminnelse. today = ISO-dato (YYYY-MM-DD).
export function nextFlightBanner(schedule, todayISO) {
  const MONTH = { jan: 0, feb: 1, mar: 2, apr: 3, mai: 4, jun: 5, jul: 6, aug: 7, sep: 8, okt: 9, nov: 10, des: 11 };
  const parse = (f) => {
    const m = /(\d{1,2})\s+(\w{3})\s+(\d{4})/.exec(f.date); if (!m) return null;
    const [hh, mm] = (f.dep || "00:00").split(":").map(Number);
    return new Date(+m[3], MONTH[m[2].toLowerCase()] ?? 0, +m[1], hh || 0, mm || 0);
  };
  const now = todayISO ? new Date(todayISO + "T00:00:00") : new Date();
  const upcoming = schedule.map(f => ({ f, t: parse(f) })).filter(x => x.t && x.t >= new Date(now.getFullYear(), now.getMonth(), now.getDate()))
    .sort((a, b) => a.t - b.t)[0];
  if (!upcoming) return "";
  const days = Math.round((new Date(upcoming.t.getFullYear(), upcoming.t.getMonth(), upcoming.t.getDate()) - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
  const when = days === 0 ? "i dag" : days === 1 ? "i morgen" : `om ${days} dager`;
  const f = upcoming.f;
  const checkin = days <= 1 ? `<div style="font-size:11px;color:var(--sky);margin-top:5px">🟢 Online innsjekk er åpen (24 t før). Møt <strong>3 t før</strong> for utenlandsfly.</div>` : `<div style="font-size:11px;color:var(--t3);margin-top:5px">Online innsjekk åpner 24 t før avgang.</div>`;
  return `<div style="background:rgba(56,189,248,.08);border:1px solid rgba(56,189,248,.22);border-radius:var(--r2);padding:11px 13px;margin-bottom:12px">
      <div style="font-size:11px;color:var(--sky);font-weight:800;text-transform:uppercase;letter-spacing:.5px">✈️ Neste fly · ${when}</div>
      <div style="font-size:13.5px;font-weight:700;color:var(--t1);margin-top:3px">${esc(f.num)} ${esc(f.from)}→${esc(f.to)} · ${esc(f.date)} ${esc(f.dep)}</div>
      ${checkin}
    </div>`;
}
